import React, { useCallback, Fragment, useState, useMemo, useEffect, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';
import { HistoryStackContext } from 'app/home/general-components/HistoryStackContext';

import Popup, { usePopup } from 'app/home/general-components/Popup/Popup';
import TimePicker, { TimePickerType } from 'app/home/general-components/TouchPicker/TimePicker';
import { markupSeconds } from 'app/home/general-components/TimeMarkup/TimeMarkup';
import Toggle, { ToggleHeadIconType } from 'app/home/general-components/Toggle/Toggle';
import { getBindInfo } from 'app/model/BindData';
import CustomIconBack from 'app/home/home-components/CustomIconBack';
import { BindConditionType } from 'app/model/Bind';
import { getTimezone } from 'app/localization/Zone';

import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconAdd } from 'images/general/icon-add-black.svg';
import { ReactComponent as IconAddHinava } from 'images/general/icon-add-hinava.svg';
import { ReactComponent as IconDelete } from 'images/general/delete.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';
import { ReactComponent as IconDeleteRed } from 'images/general/delete-red.svg';
import { ReactComponent as IconDeleteWhite } from 'images/general/delete-white.svg';
import { ReactComponent as IconWwarningCircleWhite } from 'images/general/icon-warning-circle-white.svg';
import { ReactComponent as IconSettingsGear } from 'images/setting/settings-gear.svg';

import { ReactComponent as IconDuration } from 'images/bind/duration.svg';
import { ReactComponent as IconDelay } from 'images/bind/delay.svg';
import { ReactComponent as IconDayOfWeek } from 'images/bind/day-of-week.svg';
import { ReactComponent as IconDurationBlack } from 'images/bind/duration-black.svg';
import { ReactComponent as IconDelayBlack } from 'images/bind/delay-black.svg';
import { ReactComponent as IconTimezone } from 'images/bind/timezone.svg';

import './Main.scss'

export default function Main({
    editMode, preBind, setPreBind, setPageData, dismissHandler, onFinish,
    bindName, setBindName, negotiatedProtocol
}) {
    const s = useLocalizedStrings();
    const b = s.createBindProcess;

    const [conditionInfos, actionInfos] = [preBind.conditionInfos, preBind.actionInfos];
    const canShowDoneButton = (conditionInfos.length > 0) && (actionInfos.length > 0);
    const isComplete = (conditionInfos.length > 0) && (actionInfos.length > 0) && (bindName.length > 0);

    const onAddConditionClick = useCallback(() => {
        setPageData({ name: 'select-event-type', stage: 'if' });
    }, [setPageData]);
    const onRemoveConditionClick = useCallback((index) => {
        const _preBind = { ...preBind };
        let _list = [...preBind.conditionInfos];
        _list.splice(index, 1);
        _preBind.conditionInfos = _list;
        setPreBind(_preBind);
    }, [preBind, setPreBind]);

    const onAddStatementClick = useCallback(() => {
        setPageData({ name: 'select-event-type', stage: 'then' });
    }, [setPageData]);
    const onRemoveStatementClick = useCallback((index) => {
        const _preBind = { ...preBind };
        let _list = [...preBind.actionInfos];
        _list.splice(index, 1);
        _preBind.actionInfos = _list;
        setPreBind(_preBind);
    }, [preBind, setPreBind]);

    const doneClick = useCallback(() => {
        const _preBind = { ...preBind };
        _preBind.name = bindName;
        if (isComplete) {
            onFinish(_preBind);
            dismissHandler();
        }
    }, [dismissHandler, preBind, onFinish, isComplete, bindName]);

    const [advancedPage, setAdvancesPage] = useState(false);

    return (
        <div className="create-bind-page-content main">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismissHandler} />
            <div className="header">{(editMode === true) ? b.editAutomation : b.createAutomation}</div>
            <div className="info">{b.createAutomationInfo}</div>

            <div className="if-then-lable-container if">
                <div className="text">{b.if}</div>
                {(conditionInfos.length > 1) ? <div className="info">{b.ifManyInfo}</div> : null}
            </div>
            <Condition
                conditionInfos={conditionInfos}
                editable={true}
                onAddConditionClick={onAddConditionClick}
                onRemoveConditionClick={onRemoveConditionClick} />
            <div className="if-then-lable-container then">
                <div className="text">{b.then}</div>
                {(actionInfos.length > 1) ? <div className="info">{b.thenManyInfo}</div> : null}
            </div>
            <Statement
                actionInfos={actionInfos}
                conditionInfos={conditionInfos}
                onAddStatementClick={onAddStatementClick}
                editable={true}
                onRemoveStatementClick={onRemoveStatementClick} />

            {
                ((actionInfos.length > 0) && (conditionInfos.length > 0)) ?
                    <Fragment>
                        <div className="name-section-label">{b.name}</div>
                        <div className="input-box-container" style={{ gridTemplateColumns: "1fr" }}>
                            <input className="input-box" placeholder={b.namePlaceHolder}
                                value={bindName} onChange={e => setBindName(e.target.value)}></input>
                        </div>
                    </Fragment> : null
            }


            <AdvancedSection
                preBind={preBind} setPreBind={setPreBind} setAdvancesPage={setAdvancesPage}
                negotiatedProtocol={negotiatedProtocol} />

            <div style={{ width: 10, height: canShowDoneButton ? (64 + 24) : (24) }} />

            {
                (canShowDoneButton) ? (
                    <div className={"full-blue-button" + (isComplete ? "" : " with-error")} onClick={doneClick}>
                        <div>{editMode === true ? s.save : b.create}</div>
                        <IconTick className="icon" />
                        {(isComplete) ? null : (
                            <div className="error-label"> {b.youMustEnterAName} </div>
                        )}
                    </div>
                ) : null
            }

            <AdvancedPages preBind={preBind} setPreBind={setPreBind} advancedPage={advancedPage} dismiss={() => { setAdvancesPage(false) }} />
        </div>
    );
}


export function MainDetail({ bind, devices, scenarios, dismissHandler, currentUser, negotiatedProtocol }) {
    const hs = useContext(HistoryStackContext);

    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    const [bindCache, setBindCache] = useState(bind);

    useEffect(() => {
        if (bind === undefined) {
            dismissHandler();
        } else {
            setBindCache(bind);
        }
    }, [dismissHandler, bind]);

    const [removePopupId, showRemovePopup, dismissRemovePopup] = usePopup();

    const removeClick = useCallback(() => {
        dismissRemovePopup();
        setTimeout(() => {
            dismissHandler();
            bindCache.remove();
        });
    }, [dismissHandler, dismissRemovePopup, bindCache]);

    const onEditClick = useCallback(() => {
        hs.push("/binds/create_or_edit", { editMode: true, editBindBase: bindCache });
    }, [hs, bindCache]);

    const onEnableClick = useCallback(() => {
        bindCache.setEnable(!bindCache.enable)
    }, [bindCache]);

    const showToggleLoading = (bindCache.waitingForChangeEnable && !bindCache.waitingForChangeEnable.ignoreWaiting);

    const [conditionInfos, actionInfos] = useMemo(() => {
        return getBindInfo(bindCache, devices, scenarios, l);
    }, [bindCache, devices, scenarios, l]);

    const haveError = useMemo(() => {
        let _noDay = false;
        if (bindCache.day_of_week !== undefined) {
            _noDay = bindCache.day_of_week.filter(v => v).length === 0;
        }

        let _emptyCondOrAct = (conditionInfos.length === 0) || (actionInfos.length === 0);
        return _emptyCondOrAct || (bindCache.participates_in_cycle === true) || _noDay;
    }, [bindCache, conditionInfos, actionInfos]);

    const cantChangeEnable = haveError;

    const headerGroup = useMemo(() => {
        return (
            <div className="header-group">
                <CustomIconBack className="back-icon" />
                <div />
                {
                    negotiatedProtocol.canEditBind && currentUser.permissions.bind.setting ? (
                        <Fragment>
                            <div className="button-border" onClick={onEditClick}>
                                <div className="label">{s.edit}</div>
                                <IconSettingsGear className="icon" />
                            </div>
                            <IconDeleteWhite className="remove-icon" onClick={showRemovePopup} />
                        </Fragment>
                    ) : null
                }
            </div>
        );
    }, [showRemovePopup, s.edit, onEditClick,
        negotiatedProtocol.canEditBind, currentUser]);

    return (
        <div className="create-bind-page-content">
            {/* Back Icon + Edit + Delete */}
            {headerGroup}
            <div className="nav-button" onClick={dismissHandler} />

            {/* Name + EnableToggle */}
            <div className="detail-header-box">
                <div className="header">{bindCache.name}</div>
                <div />
                {
                    (currentUser.permissions.bind.change_enable) ? (
                        <Toggle
                            onClick={onEnableClick}
                            customHeadIconType={showToggleLoading ? ToggleHeadIconType.loading : (
                                haveError ? ToggleHeadIconType.error : ToggleHeadIconType.checkOrX)}
                            unclickable={cantChangeEnable}
                            enable={bindCache.enable && !haveError} />
                    ) : null
                }
            </div>

            {bindCache.participates_in_cycle === true ?
                <div className="cylce-info">
                    <IconWwarningCircleWhite className="icon" />
                    <div className="text">{b.participatesInCycle}</div>
                </div> : null}

            <div className="if-then-lable-container if">
                <div className="text">{b.if}</div>
                {(conditionInfos.length > 1) ? <div className="info">{b.ifManyInfo}</div> : null}
            </div>
            <Condition conditionInfos={conditionInfos} editable={false} />
            <div className="if-then-lable-container then">
                <div className="text">{b.then}</div>
                {(actionInfos.length > 1) ? <div className="info">{b.thenManyInfo}</div> : null}
            </div>
            <Statement actionInfos={actionInfos} conditionInfos={conditionInfos} editable={false} />

            <AdvancedSectionOnDetailPage bind={bindCache} negotiatedProtocol={negotiatedProtocol} />

            {/* <div className="big-button red-bottom float-stick-in-bottom-button" onClick={openPopup}>
                <div>Delete</div> <IconDeleteRed className="icon" />
            </div> */}
            {/* <div style={{ height: 64 + 16 }} />
            {
                (currentUser.permissions.bind.setting) ? (
                    <div className="detele-button" onClick={openRemovePopup}>
                        <div className="label">{b.delete}</div> <IconDeleteRed className="icon" />
                    </div>
                ) : null
            } */}

            <div style={{ height: 16 }} />

            <Popup popupId={removePopupId} name="remove_bind_popup" baseZIndex={12}>
                <div className="bind-remove-popup">
                    <div className="header">{b.areYouSure}</div>
                    <div className="buttons-group">
                        <div className="delete-button" onClick={removeClick}>
                            <div className="label">{b.deleteIt}</div>
                            <IconDeleteRed className="icon" />
                        </div>
                        <div className="dismiss-button" onClick={dismissRemovePopup}>{b.keepIt}</div>
                    </div>
                </div>
            </Popup>
        </div>
    );
}

function Condition({ editable, conditionInfos, onAddConditionClick, onRemoveConditionClick }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    if ((editable) && (conditionInfos.length === 0)) {
        return (
            <div className="big-button blue-bottom" onClick={onAddConditionClick}>
                <div>{b.addCondition}</div> <IconAdd className="icon" />
            </div>
        )
    } else if ((!editable) && (conditionInfos.length === 0)) {
        return (
            <div className="empty-info">
                <IconWwarningCircleWhite className="icon" />
                <div className="text">{b.noCondition}</div>
            </div>
        )
    } else {
        return <Fragment>
            {conditionInfos.map((info, index) => (
                <div className="stage-card" key={"bind-conditionInfos-index-" + String(index)}>
                    {React.createElement(info.repr.icon, { className: "icon" })}
                    {(info.repr.lowerTitle !== undefined) ?
                        <div className="title-box">
                            <div className="upper">{info.repr.upperTitle}</div>
                            <div className="lower">{info.repr.lowerTitle}</div>
                        </div>
                        : <div className="title">{info.repr.upperTitle}</div>}
                    {editable ? <IconDeleteWhite className="delete-icon" onClick={() => onRemoveConditionClick(index)} /> : null}
                </div>
            ))}
            {editable ? (
                <div className="small-button" onClick={onAddConditionClick}>
                    <div className="text">{b.addAnotherCondition}</div> <IconAddHinava className="icon" />
                </div>
            ) : null}
        </Fragment>
    }
}

function Statement({ editable, conditionInfos, actionInfos, onAddStatementClick, onRemoveStatementClick }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    if ((editable) && (actionInfos.length === 0)) {
        if (conditionInfos.length === 0) {
            return (
                <div className="big-button blue-bottom inactive">
                    <div>{b.addStatement}</div> <IconAdd className="icon" />
                </div>
            )
        } else {
            return (
                <div className="big-button blue-bottom" onClick={onAddStatementClick}>
                    <div>{b.addStatement}</div> <IconAdd className="icon" />
                </div>
            )
        }
    } else if ((!editable) && (actionInfos.length === 0)) {
        return (
            <div className="empty-info">
                <IconWwarningCircleWhite className="icon" />
                <div className="text">{b.noStatement}</div>
            </div>
        )
    } else {
        return <Fragment>
            {actionInfos.map((info, index) => (
                <div className="stage-card" key={"bind-actionInfos-index-" + String(index)}>
                    {React.createElement(info.repr.icon, { className: "icon" })}
                    {(info.repr.lowerTitle !== undefined) ?
                        <div className="title-box">
                            <div className="upper">{info.repr.upperTitle}</div>
                            <div className="lower">{info.repr.lowerTitle}</div>
                        </div>
                        : <div className="title">{info.repr.upperTitle}</div>}
                    {editable ? <IconDeleteWhite className="delete-icon" onClick={() => onRemoveStatementClick(index)} /> : null}
                </div>
            ))}
            {editable ? (
                <div className="small-button" onClick={onAddStatementClick}>
                    <div className="text">{b.addAnotherStatement}</div> <IconAddHinava className="icon" />
                </div>
            ) : null}
        </Fragment>
    }
}

function AdvancedSection({ preBind, setPreBind, setAdvancesPage, negotiatedProtocol }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    const openDelay = useCallback(() => {
        setAdvancesPage("delay");
    }, [setAdvancesPage]);
    const openDuration = useCallback(() => {
        setAdvancesPage("duration");
    }, [setAdvancesPage]);

    const delayText = useMemo(() => {
        if (preBind.delay) {
            return markupSeconds(preBind.delay, l.localizeNumber);
        } else {
            return b.setDelay;
        }
    }, [preBind.delay, b.setDelay, l.localizeNumber]);

    const isDurationSpecified =
        (preBind.duration_from_time !== undefined) &&
        (preBind.duration_end_time !== undefined) &&
        preBind.duration_enabled;

    const durationText = useMemo(() => {
        const localizeNumber = l.localizeNumber;
        if (isDurationSpecified) {
            return localizeNumber(preBind.duration_from_time) + " " + b.to_ + " " + localizeNumber(preBind.duration_end_time);
        } else {
            return b.setPeriod;
        }
    }, [preBind.duration_from_time, preBind.duration_end_time, isDurationSpecified, b.setPeriod, b.to_, l.localizeNumber]);

    const toggleDayOfWeek = useCallback((index, newValue) => {
        const _preBind = { ...preBind };
        const _dayOfTheWeek = [..._preBind.day_of_week];
        _dayOfTheWeek[index] = newValue;
        _preBind.day_of_week = _dayOfTheWeek;
        setPreBind(_preBind);
    }, [preBind, setPreBind]);

    const resetTimezone = useCallback(() => {
        const _preBind = { ...preBind };
        _preBind.timezone = getTimezone();
        setPreBind(_preBind);
    }, [preBind, setPreBind]);

    const canResetTimezone = useMemo(() => {
        return getTimezone() !== preBind.timezone;
    }, [preBind.timezone]);

    const hasAlarm = useMemo(() => {
        return preBind.conditionInfos.filter(cond => (cond.data.condition_type === BindConditionType.alarm)).length > 0;
    }, [preBind.conditionInfos]);

    const [allDays, noDay] = useMemo(() => {
        if (preBind.day_of_week === undefined) {
            return [false, false];
        }
        let _allDays, _noDay;
        for (let i = 0; i < preBind.day_of_week.length; i++) {
            const day = preBind.day_of_week[i];
            if (day === false) {
                _allDays = false;
            }
            if (day === true) {
                _noDay = false;
            }
        }
        return [_allDays, _noDay]
    }, [preBind.day_of_week]);

    const showTimezone = negotiatedProtocol.supportTimezoneInBind && (
        (negotiatedProtocol.supportDayOfWeekInBind && !allDays && !noDay) ||
        isDurationSpecified ||
        hasAlarm
    );

    if ((preBind.actionInfos.length === 0) || (preBind.conditionInfos.length === 0)) {
        return (
            null
        );
    } else {
        return (
            <Fragment>
                {negotiatedProtocol.supportDayOfWeekInBind ? <Fragment>
                    <div className="advanced-section-label">{b.dayOfTheWeek.title}</div>
                    <div className="day-of-week">
                        {preBind.day_of_week.map((value, index) =>
                            <div
                                onClick={() => { toggleDayOfWeek(index, !value) }}
                                className={"item " + (value ? "selected" : "not-selected")}
                                key={`bind_week_${index}`}>{b.dayOfTheWeek.days[index]}</div>
                        )}
                    </div>
                </Fragment> : null}

                <div className="advanced-section-label">{b.advancedOptions}</div>
                <div className="advanced-section">
                    <div className={preBind.delay > 0 ? "filled-box" : "box"} onClick={openDelay}>
                        <div className="label" onClick={openDelay}>{delayText}</div>
                        {preBind.delay > 0 ?
                            <IconDelayBlack className="icon" onClick={openDelay} />
                            : <IconDelay className="icon" onClick={openDelay} />}
                    </div>
                    <div className={isDurationSpecified ? "filled-box" : "box"} onClick={openDuration}>
                        <div className="label" onClick={openDuration}>{durationText}</div>
                        {isDurationSpecified ?
                            <IconDurationBlack className="icon" onClick={openDuration} />
                            : <IconDuration className="icon" onClick={openDuration} />}
                    </div>
                </div>

                {showTimezone ? <Fragment>
                    <div className="advanced-section-label">{b.timezone}</div>
                    {canResetTimezone ?
                        <div className="advance-group-on-detail with-button">
                            <IconTimezone className="icon" />
                            <div>{timezoneToStr(s, preBind.timezone)}</div>
                            <div />
                            <div className="border" onClick={resetTimezone}>
                                <div className="label">{s.timezoneReset}</div>
                                <IconSettingsGear className="icon" />
                            </div>
                        </div> : <div className="advance-group-on-detail">
                            <IconTimezone className="icon" />
                            <div>{timezoneToStr(s, preBind.timezone)}</div>
                        </div>
                    }
                </Fragment> : null}
            </Fragment>
        )
    }
}

function timezoneToStr(s, timezone) {
    if ((timezone === undefined) || (timezone === "")) {
        timezone = "undefined";
    }
    if (s.timezoneStr[timezone] !== undefined) {
        return s.timezoneStr[timezone];
    } else {
        return timezone;
    }
}

function AdvancedSectionOnDetailPage({ bind, negotiatedProtocol }) {
    const l = useContext(LocalizationContext);
    const s = l.strings;
    const b = s.createBindProcess;

    const isDurationSpecified =
        (bind.duration_from_time !== undefined) &&
        (bind.duration_end_time !== undefined) &&
        bind.duration_enabled;
    const delayText = useMemo(() => {
        return markupSeconds(bind.delay, l.localizeNumber);
    }, [bind.delay, l.localizeNumber]);
    const durationText = useMemo(() => {
        const localizeNumber = l.localizeNumber;
        return localizeNumber(bind.duration_from_time) + " " + b.to_ + " " + localizeNumber(bind.duration_end_time);
    }, [bind.duration_from_time, bind.duration_end_time, b.to_, l.localizeNumber]);

    const [noDay, allDays, dayOfWeekText] = useMemo(() => {
        let dayOfWeek = bind.day_of_week;
        if (dayOfWeek === undefined)
            dayOfWeek = [true, true, true, true, true, true, true];

        const selectedIndices = [];
        for (let i = 0; i < dayOfWeek.length; i++) {
            const value = dayOfWeek[i];
            if (value)
                selectedIndices.push(i);
        }

        if (selectedIndices.length === 7) {
            return [false, true, b.dayOfTheWeek.allDays];
        } else if (selectedIndices.length > 0) {
            let innerText = b.dayOfTheWeek.daysLong[selectedIndices[0]];
            for (let i = 1; i < selectedIndices.length; i++) {
                const index = selectedIndices[i];
                if (selectedIndices.length === 2) {
                    innerText += b.dayOfTheWeek.and2 + b.dayOfTheWeek.daysLong[index];
                } else {
                    if (i === selectedIndices.length - 1) {
                        innerText += b.dayOfTheWeek.andM + b.dayOfTheWeek.daysLong[index];
                    } else {
                        innerText += b.dayOfTheWeek.comma + b.dayOfTheWeek.daysLong[index];
                    }
                }
            }
            return [false, false, b.dayOfTheWeek.selectiveDays(innerText)];
        }
        return [true, false, b.dayOfTheWeek.noday];
    }, [bind.day_of_week, b]);

    const hasAlarm = useMemo(() => {
        return bind.conditions.filter(cond => (cond.condition_type === BindConditionType.alarm)).length > 0;
    }, [bind.conditions]);

    const showTimezone = negotiatedProtocol.supportTimezoneInBind && (
        (negotiatedProtocol.supportDayOfWeekInBind && !allDays && !noDay) ||
        isDurationSpecified ||
        hasAlarm
    );

    return (
        <Fragment>
            {
                negotiatedProtocol.supportDayOfWeekInBind ? (
                    <Fragment>
                        <div className="header2 advance-label-on-detail">{b.dayOfTheWeek.title}</div>
                        <div className="advance-group-on-detail">
                            <IconDayOfWeek className="icon" />
                            <div>{dayOfWeekText}</div>
                        </div>
                        {noDay ? <div className="empty-info" style={{ marginTop: 8 }}>
                            <IconWwarningCircleWhite className="icon" />
                            <div className="text">{b.dayOfTheWeek.nodayEdit}</div>
                        </div> : null}
                    </Fragment>
                ) : null
            }
            {
                bind.delay > 0 && (
                    <Fragment>
                        <div className="header2 advance-label-on-detail">{b.delay}</div>
                        <div className="advance-group-on-detail">
                            <IconDelay className="icon" />
                            <div>{delayText}</div>
                        </div>
                    </Fragment>
                )
            }
            {
                isDurationSpecified ? (
                    <Fragment>
                        <div className="header2 advance-label-on-detail">{b.period}</div>
                        <div className="advance-group-on-detail">
                            <IconDuration className="icon" />
                            <div>{durationText}</div>
                        </div>
                    </Fragment>
                ) : null
            }
            {
                showTimezone ? (
                    <Fragment>
                        <div className="header2 advance-label-on-detail">{b.timezone}</div>
                        <div className="advance-group-on-detail">
                            <IconTimezone className="icon" />
                            <div>{timezoneToStr(s, bind.timezone)}</div>
                        </div>
                    </Fragment>
                ) : null
            }
        </Fragment>
    );
}

function AdvancedPages({ preBind, setPreBind, advancedPage, dismiss }) {
    const show = (advancedPage !== false);
    return (
        <AnimatePresence>
            {show && (<motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={{
                    initial: {
                        opacity: 0
                    },
                    animate: {
                        opacity: 1
                    },
                    exit: {
                        opacity: 0
                    }
                }}
                transition={{
                    ease: "easeInOut",
                    duration: 0.65,
                }}
                className="advanced-page-back"
                onClick={dismiss}
                key="bind-advanced-page-back"
            />)}
            {show && (<motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={{
                    initial: {
                        transform: "translateY(100%)"
                    },
                    animate: {
                        transform: "translateY(0%)"
                    },
                    exit: {
                        transform: "translateY(100%)"
                    }
                }}
                transition={{
                    ease: "easeInOut",
                    duration: 0.65,
                }}
                className="advanced-page-front"
                key="bind-advanced-page-front"
            >
                {(advancedPage === "delay") && (<DelayPage preBind={preBind} setPreBind={setPreBind} dismiss={dismiss} />)}
                {(advancedPage === "duration") && (<DurationPage preBind={preBind} setPreBind={setPreBind} dismiss={dismiss} />)}
            </motion.div>)}
        </AnimatePresence>
    )
}

function DelayPage({ preBind, setPreBind, dismiss }) {
    const s = useLocalizedStrings();
    const b = s.createBindProcess;

    const [delay, setDelay] = useState(0);

    const timeSelectedCallback = useCallback((h, m, s) => {
        setDelay(s + m * 60);
    }, []);

    const sumbit = useCallback(() => {
        preBind.delay = delay;
        setPreBind(preBind);
        dismiss();
    }, [preBind, dismiss, setPreBind, delay]);

    const clear = useCallback(() => {
        preBind.delay = 0;
        setPreBind(preBind);
        dismiss();
    }, [preBind, dismiss, setPreBind]);

    return (
        <Fragment>
            <div className="header">{b.setDelayPageTitle}</div>
            <TimePicker pickerName="delay_time_picker" type={TimePickerType.minute_second}
                className="bind-delay-picker"
                height={231} eachPickerWidth={80} baseFontSize={64}
                defaultHours={0} defaultMinutes={preBind.delay ? Math.min(Math.floor(preBind.delay / 60), 60) : 0} defaultseconds={preBind.delay ? preBind.delay % 60 : 0}
                timeSelectedCallback={timeSelectedCallback} />
            <SetAndClearButtons setCallback={sumbit} clearCallback={clear}>
                <div className="label" >{b.setDelayButton}</div>
                <IconDelay className="icon" />
            </SetAndClearButtons>
        </Fragment>
    )
}

function timeToHHmm(h, m) {
    let t = "";
    if (h < 10) {
        t += "0" + String(h);
    } else {
        t += String(h);
    }
    t += ":"
    if (m < 10) {
        t += "0" + String(m);
    } else {
        t += String(m);
    }
    return t;
}

function DurationPage({ preBind, setPreBind, dismiss }) {
    const s = useLocalizedStrings();
    const b = s.createBindProcess;

    const defaultFromTimeH = (preBind.duration_from_time !== undefined) ? parseInt(preBind.duration_from_time.split(":")[0]) : 0
    const defaultFromTimeM = (preBind.duration_from_time !== undefined) ? parseInt(preBind.duration_from_time.split(":")[1]) : 0

    const defaultEndTimeH = (preBind.duration_end_time !== undefined) ? parseInt(preBind.duration_end_time.split(":")[0]) : 0
    const defaultEndTimeM = (preBind.duration_end_time !== undefined) ? parseInt(preBind.duration_end_time.split(":")[1]) : 0

    const [fromTimeH, setFromTimeH] = useState(defaultFromTimeH);
    const [fromTimeM, setFromTimeM] = useState(defaultFromTimeM);
    const [endTimeH, setEndTimeH] = useState(defaultEndTimeH);
    const [endTimeM, setEndTimeM] = useState(defaultEndTimeM);

    const fromTimeSelectedCallback = useCallback((h, m, s) => {
        setFromTimeH(h);
        setFromTimeM(m);
    }, []);

    const endTimeSelectedCallback = useCallback((h, m, s) => {
        setEndTimeH(h);
        setEndTimeM(m);
    }, []);

    const sumbit = useCallback(() => {
        if ((fromTimeH === endTimeH) && (fromTimeM === endTimeM)) {
            preBind.duration_from_time = undefined;
            preBind.duration_end_time = undefined;
            preBind.duration_enabled = false;
        } else {
            preBind.duration_from_time = timeToHHmm(fromTimeH, fromTimeM);
            preBind.duration_end_time = timeToHHmm(endTimeH, endTimeM);
            preBind.duration_enabled = true;
        }
        setPreBind(preBind);
        dismiss();
    }, [preBind, dismiss, setPreBind,
        fromTimeH, fromTimeM, endTimeH, endTimeM,
    ]);

    const clear = useCallback(() => {
        preBind.duration_from_time = undefined;
        preBind.duration_end_time = undefined;
        preBind.duration_enabled = false;
        setPreBind(preBind);
        dismiss();
    }, [preBind, dismiss, setPreBind]);

    return (
        <Fragment>
            <div className="header">{b.setPeriodPageTitle}</div>

            <div className="duration-pickers">
                <TimePicker pickerName="from_duration_time_picker" type={TimePickerType.hour_minute}
                    className="bind-duration-picker"
                    height={138} eachPickerWidth={45} baseFontSize={32}
                    defaultHours={defaultFromTimeH} defaultMinutes={defaultFromTimeM} defaultseconds={0}
                    timeSelectedCallback={fromTimeSelectedCallback} />
                <div className="label">{b.to}</div>
                <TimePicker pickerName="to_duration_time_picker" type={TimePickerType.hour_minute}
                    className="bind-duration-picker"
                    height={138} eachPickerWidth={45} baseFontSize={32}
                    defaultHours={defaultEndTimeH} defaultMinutes={defaultEndTimeM} defaultseconds={0}
                    timeSelectedCallback={endTimeSelectedCallback} />
            </div>

            <SetAndClearButtons setCallback={sumbit} clearCallback={clear}>
                <div className="label" >{b.setPeriodButton}</div>
                <IconDuration className="icon" />
            </SetAndClearButtons>
        </Fragment>
    )
}

function SetAndClearButtons({ setCallback, clearCallback, children }) {
    const s = useLocalizedStrings();
    const b = s.createBindProcess;

    return (
        <div className="set-clear-buttons-group">
            <div className="set-button" onClick={setCallback}>
                {children}
            </div>
            <div className="clear-button" onClick={clearCallback}>
                <div className="label">{b.clear}</div>
                <IconDelete className="icon" />
            </div>
        </div>
    );
}